import React from 'react';
import { Mail, MapPin, Phone } from 'lucide-react';
import Layout from '../components/Layout';
import { useEffect } from 'react';

const AboutUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#2A382A] via-[#2A382A] to-[#1F2B1F] text-white">
        {/* Hero Section */}
        <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-24 md:py-32">
          <div className="relative z-10 max-w-6xl mx-auto text-center">
            <h1 className="font-barrio text-3xl md:text-4xl lg:text-5xl mb-8 text-white">
              We bring passion and expertise
              <br />
              to Costa Rica's wildlife
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-4xl mx-auto">
              Our passion drives us to share Costa Rica's incredible biodiversity,
              <br className="hidden md:block" />
              while contributing to wildlife research and conservation.
            </p>
          </div>

          {/* Guide Cards */}
          <div className="mt-16 md:mt-24 grid grid-cols-2 lg:grid-cols-4 gap-4 px-4 relative z-10 mb-[-18rem] max-w-4xl mx-auto">
            {[
              {
                name: "Raby Nuñez E.",
                image: "/assets/guides/raby-nunez-e-min.webp",
                scrollTo: "Raby"
              },
              {
                name: "Danixa Peña Villalobos",
                image: "/assets/guides/danixa-pena-villalobos-min.webp",
                scrollTo: "Danixa"
              },
              {
                name: "Jeffrey González Espinoza",
                image: "/assets/guides/jeffrey-gonzalez-espinoza-min.webp",
                scrollTo: "Jeffrey"
              },
              {
                name: "Isaac Perez Acuña",
                image: "/assets/guides/isaac-perez-acuna-min.webp",
                scrollTo: "Isaac"
              }
            ].map((guide, index) => (
              <button
                key={index}
                onClick={() => {
                  const section = document.getElementById(`guide-${guide.scrollTo.toLowerCase()}`);
                  section?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-center flex flex-col items-center max-w-[200px] mx-auto w-full"
              >
                <img
                  src={guide.image}
                  alt={guide.name}
                  className="aspect-[3/4] w-full object-cover rounded-lg transition-transform duration-500 hover:scale-105 mb-4 max-h-[280px]"
                />
                <p className="text-base font-barrio text-[#E85C0D] text-center">{guide.name}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="container max-w-[95%] xl:max-w-[60vw] mx-auto px-4 pt-40 pb-16 bg-white rounded-t-[2rem] text-[#2A382A]">
          <div className="prose prose-lg prose-headings:text-[#2A382A] prose-p:text-[#2A382A]/80">
            {/* Who We Are */}
            <section className="mb-12">
              <h2 className="font-barrio text-3xl mb-6">Who We Are</h2>
              <p className="text-lg mb-8">
                The Sierpefrogs' team is a group of passionate naturalist guides, wildlife photographers and researchers who 
                recognized the need for a locally-run company that truly understands the front lines of the ecotourism industry. 
                With over 17 years of experience in tourism, we are dedicated to offering amazing and personalized tours that 
                showcase the beauty of our region. Our team is made up of humble individuals who are always eager to teach and 
                learn, ensuring that every experience is both enriching and memorable.
              </p>
            </section>

            {/* Mission */}
            <section className="mb-12">
              <h2 className="font-barrio text-3xl mb-6">Our Mission</h2>
              <p className="text-xl leading-relaxed">
                Our mission is to provide unparalleled ecotours that exceed customer expectations, offering reliable information 
                and personalized assistance to make every journey unforgettable. Sierpefrogs is dedicated to scientific research 
                and wildlife photography, documenting the region's biodiversity and supporting conservation efforts. We are committed 
                to encouraging small-scale conservation projects and involving the community in our mission to protect wildlife and 
                flora, inspiring others to help preserve our natural world for future generations.
              </p>
            </section>

            {/* Team Members */}
            <section className="space-y-16">
              {/* Raby */}
              <div id="guide-raby" className="flex flex-col md:flex-row gap-8 scroll-mt-32">
                <div className="w-full md:w-1/3 flex-shrink-0">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden flex items-center justify-center bg-black">
                    <img 
                      src="/assets/guides/raby-nunez-e-min.webp"
                      alt="Raby Nuñez E"
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mt-4 mb-4 text-[#2A382A]">Raby Nuñez E</h3>
                  <p>
                    From a young age, Raby has had a deep passion for frogs. Growing up on both the Caribbean and Pacific slopes 
                    of Costa Rica, He eventually settled with his family in Sierpe de Osa. This passion led him into the tourism 
                    industry, where he became a Tour Guide at the age of 17. Three years later, he purchased his first camera, a 
                    Nikon d40, and has since honed his photography skills. His photos have been featured in scientific journals, 
                    books, and have inspired people worldwide.
                  </p>
                  <p className="mb-4">
                    Raby has authored 20 scientific notes on the frogs, snakes, and lizards of the Osa Peninsula, rediscovering 
                    species and even adding new ones to the region's records. He is currently working on several new scientific notes.
                  </p>
                  <p className="mb-4">
                    He has worked on glass frog research for the University of Costa Rica (UCR) and the University of Bern from 
                    2021 to 2023.
                  </p>
                  <p>
                    Raby has contributed to several wildlife documentaries, including a 60-minute film for NHK Japan in 2016.
                  </p>
                </div>
              </div>

              {/* Danixa */}
              <div id="guide-danixa" className="flex flex-col md:flex-row gap-8 scroll-mt-32">
                <div className="w-full md:w-1/3 flex-shrink-0">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden flex items-center justify-center bg-black">
                    <img 
                      src="/assets/guides/danixa-pena-villalobos-min.webp"
                      alt="Danixa Peña Villalobos"
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mt-4 mb-4 text-[#2A382A]">Danixa Peña Villalobos</h3>
                  <p>
                    Dani once had a severe phobia of toads and herpetofauna in general, to the point where she couldn't even 
                    enter her house, if there was a toad. About five years ago, she began to overcome her fears and managed to 
                    touch her first frog—a baby Black and Green Poison Frog (Dendrobates auratus), which soon became her favorite 
                    species.
                  </p>
                  <p>
                    Since then, Dani has immersed herself in learning about wildlife, transforming her phobia into a deep passion 
                    for these animals. Now, Dani is a coauthor of several scientific notes and works part-time with Sierpefrogs 
                    while balancing another job. She has a sharp eye for spotting species during night tours and brings an 
                    incredible passion to her work. Dani's journey is a powerful example of how knowledge can conquer fear, 
                    helping her to not only overcome her phobia but also to discover a passion she never knew she had.
                  </p>
                </div>
              </div>

              {/* Jeffrey */}
              <div id="guide-jeffrey" className="flex flex-col md:flex-row gap-8 scroll-mt-32">
                <div className="w-full md:w-1/3 flex-shrink-0">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden flex items-center justify-center bg-black">
                    <img 
                      src="/assets/guides/jeffrey-gonzalez-espinoza-min.webp"
                      alt="Jeffrey González Espinoza"
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mt-4 mb-4 text-[#2A382A]">Jeffrey González Espinoza</h3>
                  <p>
                    Since childhood, I've had a deep fascination with photography. I would often take photos with my cell phone 
                    and edit them. Four and a half years ago, I bought my first camera and began exploring nature photography. 
                    My love for macro photography started during a nocturnal hike with friends, which opened a new chapter in my 
                    photographic journey.
                  </p>
                  <p>
                    Nearly two years ago, I began working with Sierpefrogs, marking my entry into tourism and herpetofauna 
                    conservation. During this time, I've participated in scientific research focused on glass frogs and scientific 
                    notes. One of my goals is to inspire conservation through my photography, and I've gradually been able to shift 
                    the perspectives of those around me. What began as a nighttime hobby of searching for critters has now evolved 
                    into meaningful participation in research and conservation projects.
                  </p>
                </div>
              </div>

              {/* Isaac */}
              <div id="guide-isaac" className="flex flex-col md:flex-row gap-8 scroll-mt-32">
                <div className="w-full md:w-1/3 flex-shrink-0">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden flex items-center justify-center bg-black">
                    <img 
                      src="/assets/guides/isaac-perez-acuna-min.webp"
                      alt="Isaac Perez Acuña"
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mt-4 mb-4 text-[#2A382A]">Isaac Pérez Acuña</h3>
                  <p>
                    Isaac Pérez A. grew up in a farming family surrounded by lush Rain forests. From a young age, he was eager 
                    to put on rubber boots and venture into the forest, exploring its hidden treasures, from tiny frogs to 
                    towering trees. It wasn't until his final year of high school, during a snake presentation by visiting tour 
                    guides, that he discovered guiding as a potential career. This realization inspired him to pursue certification 
                    as a tour guide, combining his passion for wildlife with his professional aspirations.
                  </p>
                  <p>
                    Since 2019, Isaac has been guiding visitors and sharing the natural wonders of Costa Rica, enriching their 
                    understanding of its extraordinary biodiversity. Through his work with Sierpefrogs, he has gained invaluable 
                    knowledge of herpetofauna and honed his skills in wildlife photography. His dedication has led to co-authoring 
                    several scientific notes and contributing unique photographic documentation that has advanced scientific 
                    understanding. Additionally, Isaac has served as a field assistant in a glass frog research program conducted 
                    in collaboration with the University of Bern and the University of Costa Rica.
                  </p>
                  <p>
                    With his expertise and commitment to conservation, Isaac continues to inspire others.
                  </p>
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AboutUs;